import { useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import CommonButton from "../../ui/CommonButton/CommonButton";
import classes from "./Equipment.module.css";
import { useAccreditation } from "../../../hooks/useAccreditation";

const Equipment = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  const { formData, setFormData, loading, hydrated, fetching, editable, setEditable, handleDone } =
    useAccreditation({
      labId,
      section: "Equipment",
      name: "SADCAS F134-B",
      auditorId: user._id,
      isAssessor: user.role === "ASSESSOR",
      isEditing,
      token,
    });

  const handleAnswerChange = (questionId, score, comment, points, content) => {
    setFormData((prev) => ({
      ...prev,
      [questionId]: { score, comment, points, content },
    }));
  };

  const questionSets = [
    {
      questions1: [
        {
          questionId: "Q1",
          content:
            "a. Does the laboratory have processes for selection, procurement, installation, acceptance testing, handling, transport, storage, use, maintenance and decommissioning of equipment to ensure proper functioning and prevent contamination or deterioration?",
          number: "6.4.1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q2",
          content:
            "b. Which equipment was used to perform the examinations and was the equipment uniquely identified?",
          number: "6.3.2 (c)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q3",
          content:
            "c. Is a register maintained for equipment that can influence laboratory activities?",
          number: "",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q4",
          content:
            "d. Does the laboratory maintain and replace equipment as needed to ensure the quality of examination results?",
          number: "6.4.2 (d)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q5",
          content:
            "e. Does the laboratory have a preventive maintenance programme based on manufacturer’s schedule and instructions and are deviations recorded?",
          number: "6.4.5 (a)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q6",
          content:
            "f. Are defective equipment taken out of service clearly labelled or marked as being out of service until it has been verified to perform correctly?",
          number: "6.4.5 (c)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q7",
          content:
            "g. Does the laboratory examine the effect of the defect or deviation from specified requirements and initiate actions when non-conforming work occurs?",
          number: "6.4.5 (c ) & 7.5",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q8",
          content:
            "h. When applicable, does the laboratory decontaminate equipment before service, repair or decommissioning and provide suitable space for repairs and provide appropriate personal protective equipment?",
          number: "6.4.5 (d)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q9",
          content:
            "i. Does the laboratory have procedures for responding to any manufacturer’s recall or other notice and take actions recommended by the manufacturer?",
          number: "6.4.6",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q10",
          content:
            "j. Does the laboratory maintain records for each item of equipment which can influence the results of laboratory activities)",
          number: "6.5",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
        {
          questionId: "Q11",
          content:
            "k. Does the laboratory have procedures for the calibration of equipment that directly or indirectly affect examination results?",
          number: "6.3.3 (c)",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      questions2: [
        {
          questionId: "Comment1",
          multiline: true,
          placeholder: "Add comment",
          hideOptions: true,
        },
      ],
      render: (onAnswerChange, answers) => (
        <>
          <QuestionSection
            topQuestion={false}
            accreditation
            addWidth
            showPointsField={false}
            questions={questionSets[0].questions1}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
          <QuestionSection
            topQuestion={false}
            showQuestion={false}
            showEmptyNumber={false}
            accreditation
            showPointsField={false}
            questions={questionSets[0].questions2}
            onAnswerChange={editable ? onAnswerChange : () => {}}
            answers={answers}
            disabled={!editable}
          />
        </>
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  if (!hydrated || fetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}>
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />
          <h2 className="m-0">Clause 6.4: Equipment</h2>
        </div>
        {user.role === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => {
              setIsEditing(true);
              setEditable(true);
            }}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS B - Clause 6.4: Equipment has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, formData)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditable(false);
          }}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={loading ? "Submitting..." : "Update"}
          disabled={loading}
        />
      )}
    </div>
  );
};

export default Equipment;